/**
 * ClientIntelPanel — the rich right-hand context pane, rebuilt to match the
 * approved design preview exactly (centered avatar, status chips, action
 * circles, Authorized-units meter, Authorization & payer facts, Care &
 * schedule, Contact, Recent history, Latest note). Wired to the live
 * read-only aggregator: GET /api/ring-center/client-intel?phone=…
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MessageSquare, Video, FileText, Mail, MapPin, Building2, Users, ChevronRight, UserRound, CalendarClock, X, Plus, Pin } from "lucide-react";
import { last10 } from "../comms/useInboxRollup";
import { fileUrl } from "../../lib/utils";
import CallerHistoryDrawer from "./CallerHistoryDrawer";

const NAVY = "#032866";
const NAVY_DEEP = "#002244";
const MUTED = "#7B8FA3";
const HAIRLINE = "rgba(3,40,102,0.08)";
const NAVY_TINT = "rgba(3,40,102,0.06)";
const ROYAL = "#2A7CC4";
const RED = "#C0392B";
const AMBER = "#BA7517";
const GREEN = "#1D9E75";
const GOLD = "#FFDA00";
const FONT_HEAD = "'Manrope', sans-serif";
const FONT_BODY = "'IBM Plex Sans', sans-serif";

function initials(name, phone) {
  const s = (name || "").trim();
  if (s && /[a-z]/i.test(s)) {
    const p = s.split(/\s+/);
    return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "?";
  }
  return last10(phone).slice(-2) || "?";
}

function Fact({ k, v, warn }) {
  return (
    <div className="py-0.5 min-w-0">
      <div className="text-[10px] leading-tight" style={{ color: MUTED }}>{k}</div>
      <div className="text-[12px] font-semibold leading-tight truncate" style={{ color: warn ? AMBER : NAVY_DEEP }}>{v}</div>
    </div>
  );
}

function Facts({ items }) {
  return <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">{items.map((it) => <Fact key={it.k} {...it} />)}</div>;
}

function Section({ title, children }) {
  return (
    <div className="px-4 pt-2.5 pb-2" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="rounded-full shrink-0" style={{ width: 3, height: 10, background: GOLD }} />
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: MUTED }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// Pinned caller notes — always shown at the top of the caller panel.
function PinnedNotes({ notes, onUnpin }) {
  if (!notes || notes.length === 0) return null;
  return (
    <div className="px-4 pt-3 pb-1" data-testid="rc-pinned-notes">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Pin size={12} style={{ color: "#C9A800", fill: "#FFDA00" }} />
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: MUTED }}>Pinned notes</span>
      </div>
      <div className="space-y-1.5">
        {notes.map((n) => (
          <div key={n.id} data-testid={`rc-pinned-note-${n.id}`} className="relative text-[12px] leading-snug p-2.5 pr-7 rounded-lg" style={{ background: "rgba(255,218,0,0.14)", color: NAVY_DEEP, border: "1px solid rgba(255,218,0,0.5)" }}>
            {n.text}
            {n.by ? <div className="text-[10.5px] mt-0.5" style={{ color: MUTED }}>— {n.by}</div> : null}
            <button onClick={() => onUnpin(n.id)} data-testid={`rc-pinned-unpin-${n.id}`} title="Unpin" className="absolute top-1.5 right-1.5 w-5 h-5 grid place-items-center rounded hover:bg-black/5" style={{ color: MUTED }}><X size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sticky "Caller details" header (matches the approved mockup).
function PanelHeader() {
  return (
    <div className="sticky top-0 z-10 px-4 h-12 flex items-center gap-2 bg-white" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
      <span className="rounded-full shrink-0" style={{ width: 4, height: 16, background: GOLD }} />
      <span className="text-[14px] font-extrabold" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>Caller details</span>
    </div>
  );
}

// Labeled action card (Call / Text / Profile).
function ActionCard({ I, label, onClick, highlight, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex flex-col items-center justify-center gap-1 h-[62px] rounded-xl transition active:scale-[0.98] hover:border-[#FFDA00]"
      style={{ border: `1px solid ${highlight ? "rgba(42,124,196,0.35)" : HAIRLINE}`, background: highlight ? "rgba(42,124,196,0.08)" : "#fff", color: NAVY }}
    >
      <I size={18} strokeWidth={2} />
      <span className="text-[11px] font-semibold">{label}</span>
    </button>
  );
}

// Care-team member row with tap-to-call when a phone is on file.
function CareMember({ label, member, onCall }) {
  const name = member?.name;
  return (
    <div className="flex items-center gap-2 py-1.5">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] leading-tight" style={{ color: MUTED }}>{label}</div>
        <div className="text-[12.5px] font-semibold leading-tight truncate" style={{ color: name ? NAVY_DEEP : MUTED }}>
          {name || "Unassigned"}{member?.role && name ? ` · ${member.role}` : ""}
        </div>
      </div>
      {member?.phone && (
        <button
          onClick={() => onCall?.(member.phone)}
          className="w-8 h-8 rounded-full grid place-items-center shrink-0"
          style={{ border: `1px solid ${HAIRLINE}`, color: NAVY }}
          title={`Call ${name}`}
          data-testid="rc-context-care-call"
        >
          <Phone size={14} strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }); } catch { return iso; }
}
function timeAgoLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function ClientIntelPanel({ phone, onCall, onText }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pinnedNotes, setPinnedNotes] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);

  // Refetch when a contact is edited elsewhere (quick editor link/photo/merge)
  // so the caller panel reflects new relationships + photos without reopening.
  useEffect(() => {
    const h = () => setRefreshTick((t) => t + 1);
    window.addEventListener("avida:contact-updated", h);
    return () => window.removeEventListener("avida:contact-updated", h);
  }, []);

  const loadPinned = () => {
    if (!phone) { setPinnedNotes([]); return; }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ring-center/pinned-notes?phone=${encodeURIComponent(phone)}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    }).then((r) => (r.ok ? r.json() : { notes: [] })).then((d) => setPinnedNotes(d.notes || [])).catch(() => setPinnedNotes([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPinned(); }, [phone]);

  const unpinNote = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ring-center/history/note/${id}/pin`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        body: JSON.stringify({ pinned: false }),
      });
      setPinnedNotes((prev) => prev.filter((n) => n.id !== id));
    } catch { /* ignore transient errors */ }
  };

  useEffect(() => {
    if (!phone) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ring-center/client-intel?phone=${encodeURIComponent(phone)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        const d = r.ok ? await r.json() : null;
        if (!cancelled) { setData(d); setTags(d?.tags || []); }
      } catch { if (!cancelled) setData(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [phone, refreshTick]);

  const saveTags = async (next) => {
    setTags(next);  // optimistic
    if (!data?.patient_id) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/healthcare/patients/${data.patient_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        body: JSON.stringify({ tags: next }),
      });
    } catch { /* optimistic UI already updated; ignore transient errors */ }
  };

  const wrap = (children, testid) => (
    <div data-testid={testid} className="flex-1 min-w-0 bg-white overflow-y-auto" style={{ fontFamily: FONT_BODY, borderLeft: `1px solid ${HAIRLINE}` }}>
      {children}
    </div>
  );

  if (!phone) {
    return wrap(
      <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="rc-context-empty">
        <FileText className="w-8 h-8 mb-3" style={{ color: "#94A3B8" }} />
        <div className="text-[14px] font-semibold" style={{ color: NAVY, fontFamily: FONT_HEAD }}>No conversation selected</div>
        <div className="text-[12px] mt-1" style={{ color: MUTED }}>Open a thread or start a call to see client intelligence here.</div>
      </div>, "rc-context");
  }

  if (loading && !data) {
    return wrap(<div className="p-6 text-center text-[12.5px]" style={{ color: MUTED }} data-testid="rc-context-loading">Loading client intelligence…</div>, "rc-context");
  }

  const resolved = data?.resolved;
  const kind = data?.kind;
  const id = data?.identity || {};
  const pa = data?.authorization?.pa || null;
  const payer = data?.authorization || {};
  const care = data?.care_team || {};
  const nextVisit = data?.next_visit;
  const contact = data?.contact || {};
  const history = data?.recent_history || [];
  const note = data?.latest_note;
  const name = id.name || contact.name || formatPhoneish(phone);

  // Unknown / contact-only caller — graceful state, still shows history.
  if (!resolved || kind !== "client") {
    const cType = id.contact_type || contact.contact_type || "";
    const company = id.company || "";
    const linked = data?.linked_clients || [];
    const subtitle = kind === "contact"
      ? [cType || "Saved contact", company].filter(Boolean).join(" · ")
      : "Unknown caller";
    return wrap(
      <div data-testid={kind === "contact" ? "rc-context-contact" : "rc-context-unknown"}>
        <PanelHeader />
        <div className="px-4 pt-4 pb-3 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full grid place-items-center text-white text-lg font-bold overflow-hidden" style={{ background: kind === "contact" ? ROYAL : MUTED, boxShadow: "0 0 0 3px rgba(255,218,0,0.85)" }} data-testid="rc-context-avatar">
            {id.photo_url
              ? <img src={fileUrl(id.photo_url)} alt="" className="w-full h-full object-cover" />
              : (name && /[a-z]/i.test(name) ? initials(name, phone) : <UserRound size={22} />)}
          </div>
          <div className="mt-2 text-[15px] font-bold" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>{name}</div>
          <div className="text-[12px]" style={{ color: MUTED }}>{subtitle}</div>
          <div className="flex gap-2 mt-3">
            {[["call", Phone, "Call"], ["text", MessageSquare, "Text"]].map(([act, I, lbl], i) => (
              <button
                key={i}
                onClick={() => (act === "call" ? onCall : onText)?.(phone)}
                data-testid={`rc-context-action-${act}`}
                className="flex items-center gap-1.5 h-9 px-3 rounded-full cursor-pointer transition-colors hover:bg-[rgba(255,218,0,0.18)] hover:border-[#FFDA00]"
                style={{ border: `1px solid ${HAIRLINE}`, color: NAVY }}
              >
                <I size={15} strokeWidth={2} /> <span className="text-[12px] font-bold">{lbl}</span>
              </button>
            ))}
          </div>
        </div>

        <PinnedNotes notes={pinnedNotes} onUnpin={unpinNote} />

        {(cType || company) && (
          <Section title="Role & company">
            <Facts items={[
              { k: "Type / role", v: cType || "—" },
              { k: "Company", v: company || "—" },
            ]} />
          </Section>
        )}

        {kind === "contact" && (
          <Section title="Linked clients">
            {linked.length === 0 ? (
              <div className="text-[12px] py-1" style={{ color: MUTED }} data-testid="rc-context-linked-empty">No clients linked to this contact yet.</div>
            ) : (
              linked.map((lc) => (
                <div
                  key={lc.id}
                  onClick={() => navigate(`/healthcare/patients/${lc.id}`)}
                  className="flex items-center gap-2 py-1.5 cursor-pointer group"
                  data-testid={`rc-context-linked-${lc.id}`}
                >
                  <Users size={13} color={ROYAL} className="shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="text-[12.5px] font-semibold block truncate" style={{ color: NAVY_DEEP }}>{lc.name || "Client"}</span>
                    {lc.relationship && <span className="text-[10.5px]" style={{ color: MUTED }}>{lc.relationship}</span>}
                  </span>
                  <ChevronRight size={14} color={MUTED} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              ))
            )}
          </Section>
        )}

        {contact.phone || contact.email || contact.fax || contact.address ? (
          <Section title="Contact">
            {contact.phone && <ContactRow I={Phone}>{contact.phone}</ContactRow>}
            {contact.email && <ContactRow I={Mail}>{contact.email}</ContactRow>}
            {contact.fax && <ContactRow I={FileText}>Fax · {contact.fax}</ContactRow>}
            {contact.address && <ContactRow I={MapPin}>{contact.address}</ContactRow>}
          </Section>
        ) : null}
        <RecentHistory history={history} onViewAll={() => setHistoryOpen(true)} />
        {data?.reason && <div className="px-4 py-3 text-[11.5px]" style={{ color: MUTED }}>{data.reason}</div>}
        {historyOpen && <CallerHistoryDrawer phone={phone} name={name} onClose={() => setHistoryOpen(false)} onCall={onCall} onPinChange={loadPinned} />}
      </div>, "rc-context");
  }

  // Resolved client — the full rich panel.
  const statusChips = [];
  if (pa?.status === "approved" || pa?.status === "active") statusChips.push({ t: "Active auth", bg: "rgba(29,158,117,0.12)", c: GREEN });
  if (pa && pa.days_left != null && pa.days_left <= 30) statusChips.push({ t: "PA expiring", bg: "rgba(192,57,43,0.10)", c: RED });
  if (id.language && id.language !== "English") statusChips.push({ t: id.language, bg: NAVY_TINT, c: NAVY });
  (id.diagnoses || []).slice(0, 1).forEach(dx => statusChips.push({ t: dx, bg: NAVY_TINT, c: NAVY }));

  return wrap(
    <div data-testid="rc-context-client">
      <PanelHeader />
      <div className="px-4 pt-4 pb-3 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full grid place-items-center text-white text-xl font-bold overflow-hidden" style={{ background: ROYAL, boxShadow: "0 0 0 3px rgba(255,218,0,0.85)" }}>
          {id.photo_url ? <img src={fileUrl(id.photo_url)} alt="" className="w-full h-full object-cover" /> : initials(name, phone)}
        </div>
        <div className="mt-2 text-[16px] font-bold" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }} data-testid="rc-context-name">{name}</div>
        <div className="text-[12px] flex items-center gap-1.5" style={{ color: MUTED }}>
          <span>Client</span>
          {id.status && <><span style={{ background: GREEN }} className="w-1.5 h-1.5 rounded-full" /><span className="capitalize">{id.status}</span></>}
          {id.gender ? <span>· {id.gender}</span> : null}{id.age ? <span>· Age {id.age}</span> : null}
        </div>
        {statusChips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 justify-center mt-2">
            {statusChips.map((s, i) => <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.c }}>{s.t}</span>)}
          </div>
        )}
      </div>

      <div className="px-4 pb-3 grid grid-cols-3 gap-2">
        <ActionCard I={Phone} label="Call" onClick={() => onCall?.(phone)} testid="rc-context-call" />
        <ActionCard I={MessageSquare} label="Text" highlight onClick={() => onText?.(phone)} testid="rc-context-text" />
        <ActionCard I={UserRound} label="Profile" onClick={() => data?.patient_id && navigate(`/healthcare/patients/${data.patient_id}`)} testid="rc-context-profile" />
      </div>

      <PinnedNotes notes={pinnedNotes} onUnpin={unpinNote} />

      {pa && (
        <Section title="Authorized units">
          <div className="flex items-baseline justify-between">
            <span className="text-[19px] font-black" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>
              {pa.remaining_units}<span className="text-[12px] font-medium" style={{ color: MUTED }}> / {pa.authorized_units} hrs</span>
            </span>
            <span className="text-[11px] font-semibold" style={{ color: pa.percent_left <= 25 ? RED : GREEN }}>{pa.percent_left}% left</span>
          </div>
          <div className="h-2 rounded-full mt-1.5 overflow-hidden" style={{ background: NAVY_TINT }}>
            <div style={{ width: `${Math.max(0, Math.min(100, pa.percent_left))}%`, background: pa.percent_left <= 25 ? RED : GREEN, height: "100%" }} />
          </div>
        </Section>
      )}

      <Section title="Authorization & payer">
        <Facts items={[
          { k: "Client #", v: id.client_number || "—" },
          { k: "Medicaid ID", v: id.medicaid_id || payer.policy_number || "—" },
          { k: "Payer / MCO", v: payer.payer || "—" },
          { k: "Auth period", v: pa ? `${fmtDate(pa.start_date)} – ${fmtDate(pa.end_date)}` : "—" },
          { k: "PA expires", v: pa?.days_left != null ? `in ${pa.days_left} days` : "—", warn: pa?.days_left != null && pa.days_left <= 30 },
          { k: "Hours / week", v: pa?.hours_per_week ? `${pa.hours_per_week} hrs` : "—" },
        ]} />
      </Section>

      <Section title="Care team & schedule">
        <CareMember label="PCW / caregiver" member={care.pcw} onCall={onCall} />
        <CareMember label="RN / supervisor" member={care.rn} onCall={onCall} />
        <div className="flex items-center gap-2 py-1.5" style={{ borderTop: `1px dashed ${HAIRLINE}` }}>
          <CalendarClock size={14} color={MUTED} className="shrink-0" />
          <span className="text-[10px]" style={{ color: MUTED }}>Next visit</span>
          <span className="ml-auto text-[12px] font-semibold" style={{ color: nextVisit ? NAVY_DEEP : MUTED }}>
            {nextVisit ? `${fmtDate(nextVisit.scheduled_date)}${nextVisit.scheduled_time ? ` · ${nextVisit.scheduled_time}` : ""}` : "None scheduled"}
          </span>
        </div>
      </Section>

      {(data?.also_reachable || []).length > 0 && (
        <Section title="Also reachable">
          {data.also_reachable.map((r, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 text-[12px]" data-testid={`rc-context-reachable-${i}`}>
              <Users size={13} color={MUTED} className="shrink-0" />
              <span className="flex-1 min-w-0 truncate" style={{ color: NAVY_DEEP }}>
                <span className="font-semibold">{r.name}</span>{r.relationship ? ` · ${r.relationship}` : ""}
              </span>
              {r.phone && (
                <button onClick={() => onCall?.(r.phone)} className="w-8 h-8 rounded-full grid place-items-center shrink-0" style={{ border: `1px solid ${HAIRLINE}`, color: NAVY }} title={`Call ${r.name}`}>
                  <Phone size={14} strokeWidth={2} />
                </button>
              )}
            </div>
          ))}
        </Section>
      )}

      {(contact.phone || contact.email || contact.address) && (
        <Section title="Contact">
          {contact.phone && <ContactRow I={Phone}>{contact.phone}</ContactRow>}
          {contact.email && <ContactRow I={Mail}>{contact.email}</ContactRow>}
          {contact.address && <ContactRow I={MapPin}>{contact.address}</ContactRow>}
        </Section>
      )}

      <Section title="Tags">
        <TagEditor tags={tags} onChange={saveTags} />
      </Section>

      <RecentHistory history={history} onViewAll={() => setHistoryOpen(true)} />
      {historyOpen && <CallerHistoryDrawer phone={phone} name={name} onClose={() => setHistoryOpen(false)} onCall={onCall} onPinChange={loadPinned} />}

      {note && (
        <Section title="Latest note">
          <div className="text-[12px] leading-snug p-2.5 rounded-lg mb-3" style={{ background: NAVY_TINT, color: NAVY_DEEP }}>
            “{note.text || note}”{note.by ? ` — ${note.by}` : ""}
          </div>
        </Section>
      )}
    </div>, "rc-context");
}

function ContactRow({ I, children }) {
  return <div className="flex items-center gap-2 py-1 text-[12px]" style={{ color: NAVY_DEEP }}><I size={13} color={MUTED} /> {children}</div>;
}

function RecentHistory({ history, onViewAll }) {
  const items = history || [];
  if (items.length === 0 && !onViewAll) return null;
  const colorFor = (k) => (k === "call_missed" || k === "missed" ? RED : GOLD);
  return (
    <Section title="Recent history">
      {items.length === 0 ? (
        <div className="text-[11.5px] py-1" style={{ color: MUTED }} data-testid="rc-context-history-empty">No recent activity.</div>
      ) : items.slice(0, 6).map((h, i) => (
        <div key={i} className="flex items-center gap-2 py-1.5 text-[12px]">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colorFor(h.kind) }} />
          <span className="flex-1 truncate" style={{ color: NAVY_DEEP }} title={h.detail}>{h.label}{h.detail ? ` · ${h.detail}` : ""}</span>
          <span style={{ color: MUTED }}>{timeAgoLabel(h.at)}</span>
        </div>
      ))}
      {onViewAll && (
        <button onClick={onViewAll} data-testid="rc-context-view-history" className="mt-1.5 text-[11.5px] font-bold flex items-center gap-1 hover:opacity-80" style={{ color: ROYAL }}>
          View full history <ChevronRight size={13} />
        </button>
      )}
    </Section>
  );
}

const TAG_PRESETS = ["Personal Care", "Follow-up", "VIP", "Do not text", "Interpreter", "Prospect"];
function TagEditor({ tags, onChange }) {
  const [input, setInput] = useState("");
  const [library, setLibrary] = useState(TAG_PRESETS);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/healthcare/tag-library`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        const d = r.ok ? await r.json() : null;
        if (d?.tags?.length) setLibrary(d.tags);
      } catch { /* keep defaults */ }
    })();
  }, []);
  const add = (t) => {
    const v = (t || "").trim();
    if (!v || tags.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    onChange([...tags, v]);
    setInput("");
  };
  const remove = (t) => onChange(tags.filter((x) => x !== t));
  const suggestions = library.filter((p) => !tags.some((x) => x.toLowerCase() === p.toLowerCase()));
  return (
    <div data-testid="rc-context-tags">
      <div className="flex flex-wrap gap-1.5">
        {tags.length === 0 && <span className="text-[11.5px]" style={{ color: MUTED }}>No tags yet</span>}
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: NAVY_TINT, color: NAVY_DEEP }} data-testid={`rc-tag-${t}`}>
            {t}
            <button onClick={() => remove(t)} className="hover:opacity-70" title="Remove tag" data-testid={`rc-tag-remove-${t}`}><X size={11} /></button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(input); } }}
          placeholder="Add tag…"
          className="flex-1 min-w-0 h-8 px-2.5 rounded-lg border text-[12px] focus:outline-none"
          style={{ borderColor: HAIRLINE, color: NAVY_DEEP }}
          data-testid="rc-tag-input"
        />
        <button onClick={() => add(input)} className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ background: NAVY_DEEP, color: "#fff" }} title="Add tag" data-testid="rc-tag-add"><Plus size={14} /></button>
      </div>
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {suggestions.slice(0, 4).map((s) => (
            <button key={s} onClick={() => add(s)} className="text-[10.5px] font-medium px-2 py-0.5 rounded-full hover:bg-slate-50" style={{ border: `1px dashed ${HAIRLINE}`, color: MUTED }} data-testid={`rc-tag-suggest-${s}`}>+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function formatPhoneish(p) {
  const d = last10(p);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || "Unknown");
}
